import { Location } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';

import { BaseSiteService, OccConfig } from '@spartacus/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { DpUrlParts } from '@app/core/types/url-parts.interface';

@Injectable({ providedIn: 'root' })
export class DpUrlService {
  readonly FALLBACK_BASE_URL = 'https://localhost:9002';
  private readonly state = {
    // TODO: refactor to getter which will return Observable
    urlParts$: new BehaviorSubject({} as DpUrlParts),
    queryParams: {} as Params & { query?: string }
  };

  private allBaseSitesLanguages = new Map<string, string[]>();
  readonly #destroyRef = inject(DestroyRef);

  constructor(
    private readonly location: Location,
    private readonly occConfig: OccConfig,
    private readonly activatedRoute: ActivatedRoute,
    private readonly baseSiteService: BaseSiteService
  ) {
    this.location.onUrlChange((url) => {
      this.update(url);
    });

    this.baseSiteService
      .getActive()
      .pipe(take(1))
      .subscribe((_) => {
        this.baseSiteService
          .getAll()
          .pipe(take(1))
          .subscribe((baseSites) => {
            this.allBaseSitesLanguages = new Map(
              baseSites.map((site) => [
                site.uid ?? '',
                site.baseStore?.languages?.map((lang) => lang.isocode ?? '') ?? []
              ])
            );
          });
      });

    this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((queryParams) => {
      this.state.queryParams = queryParams;
    });
  }

  get urlParts$(): Observable<DpUrlParts> {
    return this.state.urlParts$.asObservable();
  }

  get queryParams(): Params {
    return this.state.queryParams;
  }

  get BASE_URL(): string {
    return (this.occConfig.backend?.occ?.baseUrl ?? this.FALLBACK_BASE_URL).replace(/(\/+$)/, '');
  }

  get API_ROUTE(): string {
    return `${this.BASE_URL}/occ/v2`;
  }

  get DOWNLOADS_ROUTE(): string {
    return this.BASE_URL;
  }

  getSearchTerm(): string | undefined {
    const regex = /\/search\/([\w\s:%-_]+)\??/;
    const path = this.location.path();

    if (path) {
      const term = path.match(regex)?.[1];

      if (term) {
        return decodeURI(term);
      }
    }

    return '';
  }

  parse(url: string): URL | undefined {
    let urlObject: URL;

    try {
      urlObject = new URL(url);
    } catch (error) {
      urlObject = new URL(url, this.DOWNLOADS_ROUTE);
    }

    return urlObject;
  }

  serialize(url: URL): string {
    return url.href;
  }

  update(url: string): void {
    const [, market, language, currency] = url.split('/');

    if (this.isMarketLanguageIsCorrect(market, language)) {
      this.state.urlParts$.next({
        path: url,
        market,
        language,
        currency
      });
    }
  }

  private isMarketLanguageIsCorrect(market: string, language: string): boolean {
    return (
      (this.allBaseSitesLanguages.get(market) ?? []).length === 0 ||
      !!this.allBaseSitesLanguages.get(market)?.includes(language)
    );
  }
}
